import { useCallback } from 'react';

const useDragDrop = ({
  dragOffset,
  setDragOffset,
  droppedItems,
  setDroppedItems,
  setSelectedItem,
  placeholderRef,
  placeholderWidth,
  onFileDropOnCanvas
}) => {
  // Enhanced drag and drop handlers with multiple fallback mechanisms
  const handleDragStart = (e, item) => {
    try {
      const rect = e.target.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      setDragOffset({ x: offsetX, y: offsetY });
      
      // Ensure item data is valid before stringifying
      if (item && typeof item === 'object') {
        const itemJson = JSON.stringify(item);
        
        // Set multiple data formats for better compatibility
        e.dataTransfer.setData('text/plain', itemJson);
        e.dataTransfer.setData('application/json', itemJson);
        e.dataTransfer.setData('text', itemJson);
        
        // Global fallback for browsers that clear dataTransfer
        window.draggedItem = item;
        
        // Set drag effect
        e.dataTransfer.effectAllowed = 'copy';
      } else {
        console.warn('Invalid item data for drag operation:', item);
      }
    } catch (error) {
      console.error('Error in drag start:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    // Show drop overlay when external files are dragged over canvas
    if (e.dataTransfer.types.includes('Files') && placeholderRef.current) {
      placeholderRef.current.classList.add('file-drag-over');
    }
  };

  const handleDragLeave = (e) => {
    // Only remove when leaving the placeholder itself (not children)
    if (placeholderRef.current && !placeholderRef.current.contains(e.relatedTarget)) {
      placeholderRef.current.classList.remove('file-drag-over');
    }
  };

  const handleDragEnd = (e) => {
    // Clean up global fallback
    if (window.draggedItem) {
      window.draggedItem = null;
    }
    if (placeholderRef.current) {
      placeholderRef.current.classList.remove('file-drag-over');
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    // Clear drag-over overlay
    if (placeholderRef.current) {
      placeholderRef.current.classList.remove('file-drag-over');
    }
    try {
      // Check for dropped files first (external image drop from desktop/browser)
      const files = e.dataTransfer.files;
      if (files.length > 0 && files[0].type.startsWith('image/')) {
        // File dropped on canvas → upload as person image
        if (onFileDropOnCanvas) {
          onFileDropOnCanvas(files[0]);
        }
        return;
      }

      // Otherwise, handle internal clothing card drag
      let itemData = e.dataTransfer.getData('text/plain');
      
      if (!itemData) {
        itemData = e.dataTransfer.getData('application/json');
      }
      
      if (!itemData) {
        itemData = e.dataTransfer.getData('text');
      }
      
      if (!itemData && window.draggedItem) {
        itemData = JSON.stringify(window.draggedItem);
        window.draggedItem = null;
      }
      
      if (!itemData) {
        return;
      }
      
      const item = JSON.parse(itemData);
      const rect = placeholderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - dragOffset.x;
      const y = e.clientY - rect.top - dragOffset.y;

      const { CanvasItemPlacer } = await import('../../utils/canvasItemPlacer');
      const newItem = await CanvasItemPlacer.createDragDropItem(
        item,
        x,
        y,
        droppedItems,
        placeholderWidth,
        placeholderRef.current.offsetHeight
      );

      const newItems = [...droppedItems, newItem];
      setDroppedItems(newItems, 'add-item');
      setSelectedItem(droppedItems.length);
    } catch (error) {
      console.error('Error parsing dropped item data:', error);
    }
  }, [dragOffset, droppedItems, setDroppedItems, setSelectedItem, placeholderRef, placeholderWidth, onFileDropOnCanvas]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDragEnd,
    handleDrop
  };
};

export default useDragDrop;